import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { io } from "socket.io-client";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/firebase";

const WebSocketContext = createContext(null);

// How many consecutive Unauthorized handshakes to absorb before accepting that
// the session is truly invalid (then the app stops retrying and the AuthContext
// /members/me check takes over).
const MAX_UNAUTHORIZED_RETRIES = 3;

// Firebase restores the persisted session asynchronously. After a dev
// hot-reload (or any hard refresh) the component can mount a moment before
// `auth.currentUser` exists.
//
// 2026-10-28 (Amine): the old code treated that window as "no auth" and
// returned without ever retrying, so a hot-reload could leave the tab running
// with no socket at all — the app looked disconnected (no presence, no live
// notifications) until the page was manually refreshed.
const waitForAuthReady = () =>
  new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};

    const finish = (user) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve(user || null);
    };

    unsubscribe = onAuthStateChanged(auth, (user) => finish(user));

    // Never hang a connect attempt on a stuck auth listener.
    setTimeout(() => finish(auth.currentUser), 5000);
  });

export const WebSocketProvider = ({ user, children }) => {
  const [socket, setSocket] = useState(null);
  const socketBase = useMemo(() => {
    const fallbackOrigin =
      typeof window !== "undefined" ? window.location.origin : "";
    return (
      process.env.REACT_APP_SOCKET_URL ||
      fallbackOrigin ||
      "http://localhost:5001"
    );
  }, []);

  useEffect(() => {
    // Debug: log when useEffect runs and current user id
    try {
      console.info("[WS] useEffect trigger", { userId: user?.id, socketBase });
    } catch (e) {}

    // Don't connect until we have a user ID
    if (!user?.id) {
      try {
        console.info("[WS] skipping connect - no user id available yet");
      } catch (e) {}
      return;
    }

    let cancelled = false;
    let socketInstance = null;
    // Consecutive handshakes refused for an auth reason. Reset on every
    // successful connect, so an occasional stale token never counts against a
    // healthy session.
    let unauthorizedAttempts = 0;

    // Fetch a fresh Firebase ID token for the socket handshake. The server
    // verifies this token and derives identity from it — the userId we also
    // send is only used for room membership AFTER verification.
    const fetchToken = async () => {
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) return null;
        return await firebaseUser.getIdToken();
      } catch (e) {
        console.warn("[WS] Failed to fetch Firebase ID token", e?.message || e);
        return null;
      }
    };

    const connect = async () => {
      let token = await fetchToken();

      // Auth not restored yet (typical after a dev hot-reload) — wait for it
      // rather than giving up and running without a socket.
      if (!token) {
        await waitForAuthReady();
        if (cancelled) return;
        token = await fetchToken();
      }

      if (!token) {
        console.warn("[WS] No Firebase token available - skipping socket connection");
        return;
      }

      // Create the socket connection, passing the verified token + userId for room membership
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.info("[WS] connecting with timezone", { userId: user.id, userTimeZone });
      socketInstance = io(socketBase, {
        withCredentials: true,
        auth: {
          token,
          userId: user.id,
          timeZone: userTimeZone,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
      });

      setSocket(socketInstance);

      // Basic lifecycle logging to help while learning
      socketInstance.on("connect", () => {
        unauthorizedAttempts = 0;
        console.info("[WS] connected", socketInstance.id);
        socketInstance.emit("join", { userId: user.id });

        // Always attempt to join both the user room and the linguist room for this id.
        // Joining linguist:<id> is harmless for members and ensures linguist-targeted
        // socket events reach the client even if Role metadata is missing.
        try {
          socketInstance.emit("join", { linguistId: user.id });
          console.info(`[WS] requested join linguist:${user.id}`);
        } catch (err) {
          console.warn("[WS] failed to request join linguist room", err?.message || err);
        }

        // Expose socket to window for local debugging (dev only)
        try {
          if (typeof window !== "undefined") {
            window.__socketInstance = socketInstance;
          }
        } catch (e) {
          /* ignore */
        }
      });

      socketInstance.on("disconnect", (reason) => {
        console.info("[WS] disconnected", reason);
      });

      socketInstance.on("connect_error", async (err) => {
        console.error("[WS] connect_error", err.message);

        // 2026-10-28 (Amine): a rejected token used to disable the socket for
        // the life of the tab. That happens easily in dev — the supervisor
        // restarts the server while a token sits close to its 1h expiry, so the
        // very first reconnect is refused. Refresh and retry a few times, and
        // only give up when the refusal is genuinely persistent.
        const isUnauthorized =
          typeof err.message === "string" &&
          err.message.toLowerCase().includes("unauthorized");
        if (!isUnauthorized) return;

        if (unauthorizedAttempts >= MAX_UNAUTHORIZED_RETRIES) {
          console.warn(
            "[WS] Socket auth rejected repeatedly - stopping reconnection attempts",
          );
          socketInstance?.disconnect();
          return;
        }

        unauthorizedAttempts += 1;
        const freshToken = await fetchToken();
        if (!freshToken || !socketInstance) return;

        socketInstance.auth = { ...socketInstance.auth, token: freshToken };
        // Restart immediately with the refreshed token instead of waiting out
        // the backoff and retrying with the rejected one.
        socketInstance.disconnect();
        socketInstance.connect();
      });

      socketInstance.on("reconnect_attempt", async (attemptNumber) => {
        console.info("[WS] reconnect attempt", attemptNumber);
        // Refresh the ID token before each attempt so an expired token
        // doesn't keep the connection from being re-established.
        try {
          const freshToken = await auth.currentUser?.getIdToken();
          if (freshToken && socketInstance) {
            socketInstance.auth = { ...socketInstance.auth, token: freshToken };
          }
        } catch (e) {
          console.warn("[WS] failed to refresh token for reconnect", e?.message || e);
        }
      });

      socketInstance.on("reconnect_failed", () => {
        console.error("[WS] reconnection failed after all attempts");
      });
    };

    connect();

    // Clean up on unmount
    return () => {
      cancelled = true;
      try {
        if (typeof window !== "undefined" && window.__socketInstance === socketInstance) {
          delete window.__socketInstance;
        }
      } catch (e) {}
      socketInstance?.disconnect();
    };
  }, [user?.id, socketBase]);

  const value = useMemo(() => ({ socket }), [socket]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
